import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  IndexTable,
  InlineStack,
  Pagination,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import apiClient from '../utils/api';
import RunHealthBanner, { RUN_TYPES } from './RunHealthBanner';
import RetailerOrderDetailModal from './RetailerOrderDetailModal';
import RefundsCell from './RefundsCell';
import {
  headingsFor, emptyRow, itemCountFor, PageSizeSelect, DEFAULT_PAGE_SIZE,
} from '../utils/table';

/**
 * Every order the retailer sent, as STORED, and whether each one reached a brand.
 *
 * Order ingest is two stages. Fetching writes the retailer's order into mirakl_raw_orders and
 * needs no brand connection; brand matching then splits each stored order into per-brand
 * slices and is RE-RUNNABLE, because it reads only the copy we already hold. Orders to
 * Shopify below lists the slices — what a brand received. This table lists the other side:
 * what ARRIVED, including the orders no brand received and the reason why.
 *
 * That gap is the reason the table exists. An order that reached no brand used never to be
 * written at all, so a misconfigured brand name on a connection was indistinguishable from a
 * retailer that had sent nothing. The rows are now kept either way, and the Error and Fix
 * columns say what went wrong and what to do about it.
 *
 * No state filter, deliberately. The list is every stored order, newest first, and the red
 * Error cell is what makes a problem findable — a dropdown of state names was the jargon the
 * table exists to avoid.
 */

// The whole account, always. Kept as a constant rather than inlined so it stays visible that
// the service still takes a state and this screen has chosen not to expose one.
const LIST_STATE = 'all';

const COLUMNS = [
  {
    key: 'order',
    title: 'Retailer order id',
    tip: 'The retailer\'s own order_id, as they sent it. This is the id to quote to the '
      + 'retailer and the one the per-brand rows in Orders to Shopify below are keyed by — so '
      + 'a row here with no counterpart there is an order no brand received.',
  },
  {
    key: 'status',
    title: 'Retailer status',
    tip: 'The retailer\'s order_state at the last fetch, not ours. It says nothing about '
      + 'whether the order reached a brand here: an order can sit at SHIPPING on the retailer '
      + 'and still have reached no brand. CANCELED and REFUSED are finished on arrival, so '
      + 'there is nothing to match and nothing to fix.',
  },
  {
    key: 'date',
    title: 'Order date',
    tip: 'created_date from the retailer\'s payload, with the time we fetched it underneath. '
      + 'Rows are ordered newest-first by this date, and orders whose payload carried no date '
      + 'sort last.',
  },
  {
    key: 'lines',
    title: 'Lines',
    alignment: 'end',
    tip: 'Order lines on the stored payload, and how many of them reached a brand. The two '
      + 'differ when only some lines matched. Lines the retailer sent without an offer_sku '
      + 'cannot be matched at all and are counted in the total only.',
  },
  {
    key: 'total',
    title: 'Total',
    alignment: 'end',
    tip: 'The order total the retailer sent, in the account currency, for the WHOLE order — '
      + 'not the portion belonging to any one brand. It is shown to identify the order, and '
      + 'is '
      + 'not a billing figure: commission is billed off Shopify orders, not off this row.',
  },
  {
    key: 'refunds',
    title: 'Refunds',
    tip: 'What the retailer has refunded on this order, as they sent it, and where 2W is with '
      + 'writing it to the brand\'s Shopify store. Waiting to write means the next refund run '
      + 'writes it. Refused and Write failed need a person - open the order for the reason. Not '
      + 'written by 2W is a refund 2W leaves alone on purpose: writing is off for this retailer, '
      + 'or it was settled before 2W took the retailer\'s refunds over.',
  },
  {
    key: 'brand',
    title: 'Brand',
    tip: 'The brand this order belongs to, worked out per line: the line\'s offer_sku is looked '
      + 'up in this account\'s SKU map to get the brand name the RETAILER files it under, and '
      + 'that name is matched against every active 2W brand — its name, its Mirakl brand name, '
      + 'and any connection override — trimmed and case-insensitive. A Mirakl order line '
      + 'carries no brand of its own, so this only works once offers have been fetched. Green '
      + 'is a connected brand the order can reach. Amber is a brand that exists in 2W under '
      + 'that name but has no enabled connection. Red is the retailer\'s own spelling, shown '
      + 'because no 2W brand carries it — the brand has to be created first.',
  },
  {
    key: 'error',
    title: 'Error',
    tip: 'Red means this order reached no brand, or only some of its lines did, so it cannot '
      + 'go to Shopify until it is fixed. Empty means there is nothing wrong with it. Not '
      + 'checked yet is normal for a few seconds after a fetch. Canceled or refused means the '
      + 'retailer ended the order before it got here, so there is nothing to fix. Everything '
      + 'here is recoverable from what is already stored — none of it needs a re-fetch.',
  },
  {
    key: 'note',
    title: 'Fix',
    tip: 'What to do about the error, in the order to try it: a SKU missing from this '
      + 'account\'s SKU map needs Fetch SKUs, a retailer brand name that matches no '
      + 'connection needs the connection corrected, and no connection at all means the brand '
      + 'has to be connected first. Empty when there is no error. It reports what the last '
      + 'check saw, so it can be out of date if the cause has since been fixed — press Retry '
      + 'matching to check again.',
  },
];

const when = (v) => (v ? new Date(v).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

/**
 * The brand each retailer brand name on this order resolves to — the NAME only.
 *
 * The badge carried the name plus "— needs a connection" plus the retailer's own spelling
 * underneath, which is three facts in a column headed "Brand". The tone carries the state
 * instead, and the Error and Fix columns beside it already say what went wrong and what to
 * do about it.
 *
 * Which name is shown depends on whether we found one: a resolved brand shows the 2W brand,
 * and an unresolved one shows the retailer's spelling, because that is the only name that
 * exists for it. Red therefore means "this name is not a brand in 2W", not "this brand is
 * broken".
 */
function OrderBrands({ brands }) {
  if (!Array.isArray(brands) || brands.length === 0) {
    // Nothing to resolve — no line carried an offer_sku, or no pass has run yet. Not a verdict.
    return <Text variant="bodySm" tone="subdued">—</Text>;
  }
  const tone = { attributes: 'success', unconnected: 'warning', none: 'critical' };
  return (
    <BlockStack gap="050">
      {brands.map((b) => (
        <Badge key={`${b.state}-${b.retailerName}`} tone={tone[b.state] || undefined}>
          {b.brandName || b.retailerName || '—'}
        </Badge>
      ))}
    </BlockStack>
  );
}

/**
 * What is WRONG with this order, in the words a person would use — or nothing.
 *
 * Only the two states a person can act on are red. An order that matched cleanly shows an
 * empty cell rather than a green "Attributed" badge: a column headed Error that is loud on
 * every healthy row is a column nobody reads, and the Brand column beside it already shows
 * green for the brands the order reached.
 *
 * The two non-red states are still named rather than left blank, because "nothing here" and
 * "not looked at yet" are different facts and blank cannot say which. Neither is an error, so
 * neither is toned.
 */
function OrderError({ state }) {
  if (state === 'UNATTRIBUTED') return <Badge tone="critical">No brand matched</Badge>;
  if (state === 'PARTIAL') return <Badge tone="critical">Some lines have no brand</Badge>;
  if (state === 'SKIPPED') return <Text variant="bodySm" tone="subdued">Canceled or refused</Text>;
  if (state === 'ATTRIBUTED') return <Text variant="bodySm" tone="subdued">—</Text>;
  return <Text variant="bodySm" tone="subdued">Not checked yet</Text>;
}

export default function StoredRetailerOrdersTable({ marketplaces = [] }) {
  const [marketplaceId, setMarketplaceId] = useState('');
  // Two search states: what is typed, and what has been sent. Without the split every
  // keystroke is a server page over the whole account.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Which brand received the order. Server-side: the brand is not a column on a raw order, so
  // narrowing here after paging would drop rows from a page and leave the count describing a
  // different set.
  const [brandFilter, setBrandFilter] = useState('');
  // Only orders carrying a refund, or every order. Server-side, for the same reason as the
  // brand: refunds sit inside the stored order, not in a column a page could be filtered on.
  const [refundFilter, setRefundFilter] = useState('');
  const [page, setPage] = useState(1);
  // The order whose detail dialog is open, by retailer order id. Null when closed.
  const [openOrderId, setOpenOrderId] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [notice, setNotice] = useState(null);

  // Default to the first retailer rather than "all": stored orders belong to one account's
  // credentials, and the same brand name can be spelled differently by two retailers, so a
  // merged list would put unrelated notes side by side with nothing to tell them apart.
  useEffect(() => {
    if (!marketplaceId && marketplaces.length > 0) setMarketplaceId(marketplaces[0].id);
  }, [marketplaces, marketplaceId]);

  // ~350ms. Long enough that a typed order id is one request, short enough that it does not
  // feel like the box is ignoring you.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /**
   * Only the NEWEST request may write state — the same guard, and for the same reason, as
   * SkuMapTable and QueueTable.
   *
   * The page reset is its own effect while `load` depends on `page`, so both run in one
   * commit: changing the row count on page 3 fires page 3 at the new limit AND then page 1 at
   * the new limit. Two requests, no cancellation, no ordering guarantee — so the slower one
   * won and page-3 rows sat under "Page 1 of 2".
   */
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!marketplaceId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await apiClient.getStoredRetailerOrders(marketplaceId, {
        state: LIST_STATE, search, page, limit: pageSize, brandId: brandFilter,
        refunds: refundFilter,
      });
      if (seq !== requestSeq.current) return;
      setData(res);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err.message || 'Could not load the stored retailer orders');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [marketplaceId, search, page, pageSize, brandFilter, refundFilter]);

  useEffect(() => { load(); }, [load]);

  // Any retailer, search or row-count change returns to page 1. A narrower search otherwise
  // lands on a page that no longer exists and the table reads as though nothing matched —
  // which on this table would be read as "this retailer sent nothing", the opposite of the
  // truth.
  useEffect(() => {
    setPage(1);
  }, [marketplaceId, search, pageSize, brandFilter, refundFilter]);

  // Switching retailer must not carry the previous one's notice: "12 order(s) considered"
  // sitting above a different account's rows is a false statement about those rows.
  useEffect(() => { setNotice(null); }, [marketplaceId]);

  // The brand list belongs to one account, so a brand kept across a retailer switch would
  // filter the new account by an id that has no orders on it — an empty table reading as
  // "this retailer sent nothing".
  useEffect(() => { setBrandFilter(''); }, [marketplaceId]);

  const retryMatching = useCallback(async () => {
    if (!marketplaceId) return;
    setMatching(true);
    try {
      const res = await apiClient.attributeStoredRetailerOrders(marketplaceId);
      setNotice({
        tone: (res?.unattributed ?? 0) + (res?.partial ?? 0) > 0 ? 'warning' : 'success',
        message: res?.message || 'Finished checking the stored orders.',
      });
      setError(null);
      // Reload rather than patch: the check rewrote the state and the note on every row it
      // touched, so nothing on screen is still accurate after it runs.
      await load();
    } catch (err) {
      setError(err.message || 'Could not re-check the stored orders');
    } finally {
      setMatching(false);
    }
  }, [marketplaceId, load]);

  const summary = data?.summary ?? null;
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  /**
   * Which of the two empty messages is TRUE.
   *
   * The search box can only be the explanation when there is something for it to hide.
   * `summary.all` is the account-wide count and does not move when a search is applied
   * (listRawOrders groups over marketplaceId only), so 0 there means this retailer holds no
   * stored orders at all. Guarded on the summary being present so the render before the
   * first response does not declare an account empty — otherwise a brand-new install is told
   * its search is wrong instead of being given the one instruction it needs (Fetch orders,
   * which needs no brand connection).
   */
  const nothingStored = summary != null && (summary.all ?? 0) === 0;
  const filtered = !nothingStored && (Boolean(search) || Boolean(refundFilter));

  const headings = useMemo(() => headingsFor(COLUMNS), []);

  if (marketplaces.length === 0) {
    return (
      <Text tone="subdued" variant="bodySm">
        No retailer accounts yet. Add one and fetch its orders to see what it has sent.
      </Text>
    );
  }

  return (
    <BlockStack gap="400">
      {error && <Banner tone="critical">{error}</Banner>}

      {/* An order fetch that died half way leaves this table short by exactly the orders
          it never reached — and short reads identically to "the retailer sent nothing". */}
      <RunHealthBanner marketplaceId={marketplaceId} types={RUN_TYPES.RETAILER_ORDERS} />
      {notice && (
        <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.message}</Banner>
      )}

      <InlineStack gap="300" wrap blockAlign="center">
        <Box minWidth="240px">
          <TextField
            label="Search stored orders"
            labelHidden
            placeholder="Search retailer order id"
            value={searchInput}
            onChange={setSearchInput}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearchInput('')}
          />
        </Box>
        {marketplaces.length > 1 && (
          <Select
            label="Retailer"
            labelHidden
            options={marketplaces.map((m) => ({ label: m.name, value: m.id }))}
            value={marketplaceId}
            onChange={setMarketplaceId}
          />
        )}
        {/* Only the brands that actually received an order on this account, so every option
            returns rows. It filters on orders that REACHED the brand — one that named it and
            attributed to nobody is found with the Error column, not here. */}
        {(data?.brands?.length ?? 0) > 0 && (
          <Select
            label="Brand"
            labelHidden
            options={[
              { label: 'All brands', value: '' },
              ...data.brands.map((b) => ({ label: b.name, value: b.id })),
            ]}
            value={brandFilter}
            onChange={setBrandFilter}
          />
        )}
        {/* The count is the whole account's, like the summary it comes from, so it does not
            change when the filter is applied. */}
        {summary != null && (
          <Select
            label="Refunds"
            labelHidden
            options={[
              { label: 'All orders', value: '' },
              { label: `Has refunds (${summary.refunded ?? 0})`, value: 'with' },
            ]}
            value={refundFilter}
            onChange={setRefundFilter}
          />
        )}
        {/* Not gated on any Sync Settings write toggle, deliberately. This reads our own
            database and writes only our own rows, and it is the recovery path for a
            misconfigured connection — gating it behind the switch that governs writing to a
            retailer would make fixing the mistake conditional on the thing it is blocking. */}
        <Tooltip content="Tries again to work out which brand each stored order belongs to. An order that already failed is never retried on its own, so press this after connecting a brand or correcting a brand name. Nothing is sent to the retailer or to Shopify.">
          <Button onClick={retryMatching} loading={matching} disabled={!marketplaceId}>
            Match orders to brands
          </Button>
        </Tooltip>
      </InlineStack>

      {/* No heading is sortable, so no sortToggleLabels are needed and every `tip` above
          renders: Polaris silently discards a sortable heading's tooltipContent unless that
          prop is supplied and complete. Ordering is the API's — newest order date first. */}
      <IndexTable
        resourceName={{ singular: 'stored order', plural: 'stored orders' }}
        // 1 when there is nothing, so the header row survives: at itemCount 0 IndexTable
        // swaps the WHOLE table for emptyState, column names included. Emptiness is a row.
        itemCount={itemCountFor(rows)}
        selectable={false}
        headings={headings}
        loading={loading}
      >
        {rows.length === 0
          ? emptyRow(
            COLUMNS,
            'stored-retailer-orders',
            filtered
              ? 'No order matches this search or filter. Clear the search box and set Refunds '
                + 'to All orders to see everything held for this retailer.'
              : 'No orders held for this retailer yet. Use Fetch orders on its row above — '
                + 'it reads the retailer\'s orders and needs no brand connection, so the rows '
                + 'are kept even when no brand can be matched yet.',
          )
          : rows.map((row, index) => (
            <IndexTable.Row
              id={row.id}
              key={row.id}
              position={index}
              onClick={() => setOpenOrderId(row.miraklOrderId)}
            >
              <IndexTable.Cell>
                <Text variant="bodySm" fontWeight="semibold">{row.miraklOrderId}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm">{row.miraklStatus || '—'}</Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Text variant="bodySm">{when(row.miraklCreatedDate)}</Text>
                  <Text variant="bodySm" tone="subdued">{`fetched ${when(row.fetchedAt)}`}</Text>
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <BlockStack gap="050">
                  <Text variant="bodySm" alignment="end" numeric>
                    {row.lineItemCount ?? row.linesTotal ?? '—'}
                  </Text>
                  {/* Only when they disagree. "3 of 3 attributed" on every attributed row is
                      noise that hides the rows where the numbers differ. */}
                  {row.linesTotal != null
                    && row.linesAttributed != null
                    && row.linesAttributed !== row.linesTotal && (
                    <Text variant="bodySm" tone="subdued" alignment="end">
                      {`${row.linesAttributed} of ${row.linesTotal} matched`}
                    </Text>
                  )}
                </BlockStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Text variant="bodySm" alignment="end" numeric>
                  {apiClient.formatCurrency(row.totalPrice ?? 0, row.currencyCode || undefined)}
                </Text>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <RefundsCell refunds={row.refunds} />
              </IndexTable.Cell>
              <IndexTable.Cell>
                <OrderBrands brands={row.brands} />
              </IndexTable.Cell>
              <IndexTable.Cell>
                <OrderError state={row.attributionState} />
              </IndexTable.Cell>
              {/* The fix is the cell this table is for — it is the sentence that says what to
                  do — so it gets the width and wraps in full rather than being clipped to a
                  line. The SKU and brand samples underneath name the offending values, which
                  the sentence only counts. */}
              <IndexTable.Cell>
                <Box minWidth="320px" maxWidth="560px">
                  <BlockStack gap="050">
                    <Text variant="bodySm" breakWord>
                      {row.attributionNote || '—'}
                    </Text>
                    {Array.isArray(row.unmatchedBrands) && row.unmatchedBrands.length > 0 && (
                      <Text variant="bodySm" tone="subdued" breakWord>
                        {`Retailer brand names with no connection: ${row.unmatchedBrands.join(', ')}`}
                      </Text>
                    )}
                    {Array.isArray(row.unknownSkus) && row.unknownSkus.length > 0 && (
                      <Text variant="bodySm" tone="subdued" breakWord>
                        {`SKUs not in the map: ${row.unknownSkus.slice(0, 5).join(', ')}`}
                        {row.unknownSkus.length > 5 ? ` and ${row.unknownSkus.length - 5} more` : ''}
                      </Text>
                    )}
                  </BlockStack>
                </Box>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
      </IndexTable>

      {/* The row count stays even when the pager drops out: it is how someone GETS a second
          page, so hiding it at size 50 would strand them on the first. */}
      {rows.length > 0 && (
        <InlineStack align="space-between" blockAlign="center">
          <PageSizeSelect value={pageSize} onChange={setPageSize} />
          {totalPages > 1 && (
            <Pagination
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPrevious={() => setPage((p) => Math.max(p - 1, 1))}
              onNext={() => setPage((p) => p + 1)}
              label={`Page ${page} of ${totalPages} · ${total} order${total === 1 ? '' : 's'}`}
            />
          )}
        </InlineStack>
      )}

      {/* Mounted only while open, so closing the dialog discards its state and the next row
          opens on a clean fetch rather than briefly showing the previous order. */}
      {openOrderId && (
        <RetailerOrderDetailModal
          marketplaceId={marketplaceId}
          miraklOrderId={openOrderId}
          onClose={() => setOpenOrderId(null)}
        />
      )}
    </BlockStack>
  );
}
